import type { SimEvents } from '../sim/events';
import type { PlayerInput } from '../sim/input';
import type { LevelDef } from '../sim/level/schema';
import { reloadLevel, respawnPlayers } from '../sim/systems/reset';
import { Dead, Transform } from '../sim/traits';
import type { SimHandle } from '../sim/world';
import { gameplayHelp } from '../input/bindings';
import { buttonLabel, type ControllerGlyphs, type InputDevice } from '../input/presentation';
import type { PadMap } from '../input/remap';

export const practiceLevel: LevelDef = {
  id: 'practice', name: 'Practice Yard', theme: 'arena',
  bounds: { x: 0, y: 0, w: 32, h: 18 }, killMargin: 8,
  spawns: [{ x: 7, y: 2 }, { x: 9, y: 2 }, { x: 11, y: 2 }, { x: 13, y: 2 }],
  drops: { enabled: false, xMin: 5, xMax: 27, intervalScale: 1 },
  objects: [
    { type: 'solid', x: 16, y: 0.5, w: 32, h: 1 },
    { type: 'solid', x: 0.5, y: 6, w: 1, h: 12 },
    { type: 'solid', x: 31.5, y: 6, w: 1, h: 12 },
    { type: 'solid', x: 21, y: 2.6, w: 5, h: 0.6 },
    { type: 'solid', x: 26, y: 5, w: 4, h: 0.6 },
  ],
};

export type PracticeState = { step: number; startX: number; blockTicks: number };
export function createPractice(startX: number): PracticeState { return { step: 0, startX, blockTicks: 0 }; }

/** Falling out should reset the yard without making the player repeat completed lessons. */
export function recoverPractice(state: PracticeState, sim: SimHandle): boolean {
  if (!sim.players().some((player) => player.has(Dead))) return false;
  reloadLevel(sim.ecs, practiceLevel);
  respawnPlayers(sim.ecs);
  state.startX = sim.players()[0]!.get(Transform)!.x;
  state.blockTicks = 0;
  return true;
}

/** Advance only after the player performs the action; elapsed time never completes a lesson. */
export function advancePractice(state: PracticeState, player: number, x: number, grounded: boolean, vy: number, input: PlayerInput, events: SimEvents): boolean {
  const previous = state.step;
  if (state.step === 0 && Math.abs(x - state.startX) >= 2) state.step++;
  else if (state.step === 1 && input.jump && !grounded && vy > 1) state.step++;
  else if (state.step === 2 && events.some((e) => e.type === 'shot' && e.source === player)) state.step++;
  else if (state.step === 3) {
    state.blockTicks = input.block ? state.blockTicks + 1 : 0;
    if (state.blockTicks >= 24) state.step++;
  } else if (state.step === 4 && events.some((e) => e.type === 'throw' && e.player === player)) state.step++;
  return previous !== state.step;
}

export function practicePrompt(step: number, device: InputDevice, glyphs: ControllerGlyphs = 'auto', map?: PadMap): { title: string; body: string } {
  const pad = device.kind === 'gamepad';
  const help = (action: Parameters<typeof gameplayHelp>[0]) => gameplayHelp(action, device, glyphs, map);
  return [
    { title: 'Find your feet', body: pad ? 'Move with the left stick. Walk a few steps.' : 'Press A / D to move. Walk a few steps.' },
    { title: 'Get some air', body: `Press ${help('jump')} to jump. Jump against a wall to climb it.` },
    { title: 'Aim and fire', body: `Aim with the ${pad ? 'right stick' : 'mouse'}, then use ${help('attack')} to fire.` },
    { title: 'Raise your guard', body: `Hold ${help('block')} briefly. Aim toward incoming fire; release to recharge.` },
    { title: 'Make the last shot count', body: `Press ${help('throw')} to throw your weapon. It can hit an opponent.` },
    { title: 'Ready to brawl', body: `You know the basics. Keep exploring or press ${pad ? buttonLabel(9, device, glyphs) : 'Enter'} for a Quick Match.` },
  ][Math.min(step, 5)]!;
}
